/**
 * SA-460 (workflow UX sweep spec §6.3, §7.3): the words of the record's
 * live half — the drawer's status and task lines and the times the timeline
 * shares. Beside the component, the `stepDrawerText.ts` convention. Times
 * are formatted by hand (day, short month, 24h clock; local time, or the
 * zone passed — SA-475) so a test reads the same string under any runner
 * locale; `now` is injectable.
 */
import type { TaskRead } from '@/types/process'
import type { QuoteOutcome } from '@/types/commerce'
import type { StepLiveStatus, StepLiveTask } from './stepLive'
import { exitWord } from './stepDrawerText'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const pad2 = (n: number) => String(n).padStart(2, '0')

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

/** A wall-clock reading of an instant. */
interface Parts { year: number; month: number; day: number; hour: number; minute: number }

/** SA-475 (Home spec §13 FF5): read in `zone` (IANA) when given, else in
 * the browser's local time — today's behaviour, byte for byte. `h23` so
 * midnight is 00, never 24 (the `% 24` guards an engine that ignores it). */
function partsOf(d: Date, zone?: string): Parts {
  if (zone) {
    try {
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      }).formatToParts(d)
      const n = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value)
      return { year: n('year'), month: n('month'), day: n('day'), hour: n('hour') % 24, minute: n('minute') }
    } catch {
      // SA-1078: an unknown zone reads locally rather than throwing out of a render.
    }
  }
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() }
}

const clock = (p: Parts) => `${pad2(p.hour)}:${pad2(p.minute)}`
const sameDate = (a: Parts, b: Parts) => a.year === b.year && a.month === b.month && a.day === b.day

/** The calendar date after `p`'s — a date step, never `+ 24 h` (DST). */
function nextDate(p: Parts): Parts {
  const u = new Date(Date.UTC(p.year, p.month - 1, p.day + 1))
  return { ...p, year: u.getUTCFullYear(), month: u.getUTCMonth() + 1, day: u.getUTCDate() }
}

/** "14 Sep"; the year only when it is not `now`'s. `zone` reads the day there. */
export function dayText(iso: string | null | undefined, now: Date = new Date(), zone?: string): string | null {
  const d = parse(iso)
  if (!d) return null
  const p = partsOf(d, zone)
  const year = p.year === partsOf(now, zone).year ? '' : ` ${p.year}`
  return `${p.day} ${MONTHS[p.month - 1]}${year}`
}

/** "14 Sep 11:40"; the year only when it is not `now`'s. */
export function whenText(iso: string | null | undefined, now: Date = new Date(), zone?: string): string | null {
  const d = parse(iso)
  if (!d) return null
  return `${dayText(iso, now, zone)} ${clock(partsOf(d, zone))}`
}

/**
 * "20 Oct 11:35 – 12:00" on one day, "20 Oct 23:10 – 21 Oct 01:05" across days,
 * one instant once; a missing end falls back to the other (SA-1328).
 */
export function spanText(firstIso: string | null | undefined, lastIso: string | null | undefined, now: Date = new Date(), zone?: string): string | null {
  const a = parse(firstIso) ?? parse(lastIso)
  const b = parse(lastIso) ?? a
  if (!a || !b) return null
  const pa = partsOf(a, zone)
  const pb = partsOf(b, zone)
  const start = whenText(a.toISOString(), now, zone)
  if (a.getTime() === b.getTime()) return start
  return sameDate(pa, pb) ? `${start} – ${clock(pb)}` : `${start} – ${whenText(b.toISOString(), now, zone)}`
}

/** "today 16:00", "tomorrow 09:00", else `whenText` — in `zone` when given. */
export function dueText(iso: string | null | undefined, now: Date = new Date(), zone?: string): string | null {
  const d = parse(iso)
  if (!d) return null
  const p = partsOf(d, zone)
  const n = partsOf(now, zone)
  if (sameDate(p, n)) return `today ${clock(p)}`
  if (sameDate(p, nextDate(n))) return `tomorrow ${clock(p)}`
  return whenText(iso, now, zone)
}

/** The day number of a wall-clock date, for differences between dates. */
const dayIndex = (p: Parts) => Math.floor(Date.UTC(p.year, p.month - 1, p.day) / 86_400_000)

/** SA-1078 (UX pass spec, move 3): the zone's short name, once — `EEST`, `GST`,
 * `PDT`, `UTC`. en-US first (US zones read PDT), en-GB when that is an offset
 * form (`GMT+3` → `EEST`), the offset itself when both are (`GMT+5:30`). An
 * unknown zone, or no instant, is null, never a throw. */
export function zoneAbbreviation(iso: string | null | undefined, zone?: string): string | null {
  const d = parse(iso)
  if (!d) return null
  const name = (locale: string): string | null => {
    try {
      return new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: 'short' })
        .formatToParts(d).find((p) => p.type === 'timeZoneName')?.value ?? null
    } catch {
      return null
    }
  }
  const us = name('en-US')
  if (us && !us.startsWith('GMT')) return us
  const gb = name('en-GB')
  if (gb && !gb.startsWith('GMT')) return gb
  return us ?? gb
}

/** SA-1078: the task header's due words — `dueText`'s today / tomorrow, a
 * weekday for the five days after tomorrow (`Fri 14:00`), else `whenText`'s
 * date — followed by the zone once (`today 14:00 EEST`). `dueText` itself is
 * unchanged, so every current caller keeps its bytes. */
export function dueLine(iso: string | null | undefined, now: Date = new Date(), zone?: string): string | null {
  const d = parse(iso)
  if (!d) return null
  const p = partsOf(d, zone)
  const n = partsOf(now, zone)
  const ahead = dayIndex(p) - dayIndex(n)
  const words = ahead === 0 ? `today ${clock(p)}`
    : ahead === 1 ? `tomorrow ${clock(p)}`
    : ahead > 1 && ahead <= 6 ? `${DAYS[new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()]} ${clock(p)}`
    : whenText(iso, now, zone)!
  const abbreviation = zoneAbbreviation(iso, zone)
  return abbreviation ? `${words} ${abbreviation}` : words
}

const HOUR_MS = 3_600_000
const DAY_MS = 86_400_000

/** SA-1174 (design note D4): how late an overdue task is — "due now" under an
 * hour (a due still ahead on this clock included), "N h over" under a day,
 * "N d over" after that; N floored, so a chip never reads later than the
 * truth. Null for no time or an unreadable one. */
export function overdueText(iso: string | null | undefined, now: Date = new Date()): string | null {
  const d = parse(iso)
  if (!d) return null
  const over = now.getTime() - d.getTime()
  if (over < HOUR_MS) return 'due now'
  if (over < DAY_MS) return `${Math.floor(over / HOUR_MS)} h over`
  return `${Math.floor(over / DAY_MS)} d over`
}

/** "feasibility_recorded" → "feasibility recorded". */
export function lowerWords(id: string): string {
  return id.replace(/_/g, ' ')
}

/** SA-1223: the five outcomes a decline can close a record as. Every other
 * code (and a code a newer server adds) reads as its sentence-case words.
 * Pinned to the backend's table by `shared/outcome-labels.json`. */
export const OUTCOME_LABELS: Readonly<Record<string, string>> = {
  declined_no_capacity: 'Declined: no capacity',
  declined_economics: 'Declined: economics',
  not_pursued: 'Declined: counterparty',
  declined_cargo: 'Declined: cargo',
  not_feasible: 'Declined: not feasible',
}

/** The Outcome control's choices, in the order it lists them. */
export const OUTCOME_CHOICES: readonly QuoteOutcome[] = [
  'sold', 'operated', 'not_sold', 'customer_withdrawn', 'cancelled',
  'declined_no_capacity', 'declined_economics', 'not_pursued', 'declined_cargo', 'not_feasible',
  'not_an_enquiry',
]

/** "Not an enquiry": the outcome as a label (SA-1020), beside `outcomeWords`'s lowercase run-in form. */
export function outcomeLabel(outcome: string | null | undefined): string | null {
  if (!outcome) return null
  const label = OUTCOME_LABELS[outcome]
  if (label) return label
  const words = lowerWords(outcome)
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function outcomeWords(outcome: string | null | undefined): string | null {
  if (!outcome) return null
  const label = OUTCOME_LABELS[outcome]
  return label ? label.toLowerCase() : lowerWords(outcome)
}

/** A task row's kind as a word: who did this round. */
export function kindWord(kind: TaskRead['kind']): string {
  switch (kind) {
    case 'ai':
      return 'AI'
    case 'automation':
      return 'Automation'
    case 'wait':
      return 'Wait'
    default:
      return 'Person'
  }
}

/** The drawer's status line (spec §6.3); a due time in the deployment zone, named once (SA-1418 A4). */
export function statusLine(status: StepLiveStatus, now: Date = new Date(), zone?: string): string {
  switch (status.kind) {
    case 'done': {
      const exit = status.exit ? `${exitWord(status.exit)}${status.targetLabel ? ` → ${status.targetLabel}` : ''}` : null
      return [`Done ${whenText(status.at, now) ?? ''}`.trimEnd(), status.seatLabel, exit].filter(Boolean).join(' · ')
    }
    case 'current':
      return [
        'Current',
        status.seatLabel ? `waiting on ${status.seatLabel}` : null,
        status.waitingFor,
        status.dueAt ? `due ${dueLine(status.dueAt, now, zone)}` : null,
      ].filter(Boolean).join(' · ')
    case 'skipped':
      return `Skipped · ${status.fromLabel} ended ${exitWord(status.exit)} → ${status.targetLabel}`
    case 'pending':
      return 'Not reached yet'
    case 'closed_before':
      return 'Closed before this step'
  }
}

/** The drawer's task line: who holds a LIVE task, or who completed it and
 * when. A `failed` outbox row is not live either, so it reads as completed. */
export function taskLine(task: StepLiveTask, now: Date = new Date()): string {
  if (task.status === 'open' || task.status === 'claimed') {
    if (task.assigneeName) return `assigned to ${task.assigneeName}`
    return task.assigneeId ? 'assigned' : 'unassigned'
  }
  return [`Completed${task.completedByName ? ` by ${task.completedByName}` : ''}`, whenText(task.completedAt, now)].filter(Boolean).join(' · ')
}
